/**
 * Every request the config layer makes, in one file, typed and INJECTABLE.
 *
 * The functions take an optional `ConfigApi` so a test can drive the store
 * without a network or a jsdom `fetch` shim, and so the admin console can pass
 * an aborting client. The default implementation is core's `api`, which carries
 * the cookies and the refresh-and-retry interceptor everything else uses.
 */
import { api } from '@/core/network/api';
import type { Card } from '@rga/sim';

import type { FlagAnswersView, RemoteConfigEnvelope, RemoteConfigVersionView } from './config.types';

export interface ConfigApi {
  /** `GET /config/version` — the cheap pre-match poll. */
  version(): Promise<RemoteConfigVersionView>;
  /** `GET /config` — the full document. */
  document(): Promise<RemoteConfigEnvelope>;
  /** `GET /config/flags` — the server's own answers. 401 for a signed-out caller. */
  answers(): Promise<FlagAnswersView>;
  /** `GET /config/card-table/:version` — the archived roster a replay pins. */
  cardTable(version: number): Promise<{ cardTableVersion: number; cards: readonly Card[] }>;
}

export const defaultConfigApi: ConfigApi = {
  version: () => api.get<RemoteConfigVersionView>('/config/version'),
  document: () => api.get<RemoteConfigEnvelope>('/config'),
  answers: () => api.get<FlagAnswersView>('/config/flags'),
  cardTable: (version) =>
    api.get<{ cardTableVersion: number; cards: readonly Card[] }>(
      `/config/card-table/${encodeURIComponent(String(version))}`,
    ),
};

/* ═══════════════════════════════════════════════ the operator console's half ══ */

export interface ConfigFinding {
  readonly severity: 'hard' | 'soft';
  readonly source: string;
  readonly message: string;
}

export interface ConfigValidationReport {
  readonly ok: boolean;
  readonly findings: readonly ConfigFinding[];
  readonly balanceCheckerRan: boolean;
  readonly cardsChecksum: string;
  readonly cardCount: number;
  readonly deckableCount: number;
  readonly nextCardTableVersion: number;
  readonly cardsUnchanged: boolean;
}

export interface ConfigRevisionRow {
  readonly revision: number;
  readonly cardTableVersion: number;
  readonly cardsChecksum: string;
  readonly checksum: string;
  readonly note: string;
  readonly publishedAt: string;
  readonly publishedById: string | null;
  readonly rolledBackFrom: number | null;
  readonly live: boolean;
  readonly signatureValid: boolean;
}

/** A flag WITH its operator-only fields. Only the admin document carries these. */
export interface AdminFlag {
  readonly key: string;
  readonly description: string;
  readonly enabled: boolean;
  readonly rollout: number;
  readonly allowIds: readonly string[];
  readonly denyIds: readonly string[];
}

export interface AdminConfigDocument {
  readonly revision: number;
  readonly cardTableVersion: number;
  readonly checksum: string;
  readonly cardsChecksum: string;
  readonly publishedAt: string;
  readonly note: string;
  readonly source: string;
  readonly signatureValid: boolean;
  readonly payload: {
    readonly format: number;
    readonly cardTableVersion: number;
    readonly cards: readonly Card[];
    readonly flags: readonly AdminFlag[];
  };
}

export interface PublishResponse {
  readonly report: ConfigValidationReport;
  readonly revision: ConfigRevisionRow | null;
}

export const configAdminApi = {
  document: () => api.get<AdminConfigDocument>('/config/admin/document'),
  revisions: (limit = 50) =>
    api.get<{ live: number; source: string; revisions: ConfigRevisionRow[] }>(
      `/config/admin/revisions?limit=${encodeURIComponent(String(limit))}`,
    ),
  validate: (body: { cards: unknown[]; flags?: unknown[] }) =>
    api.post<ConfigValidationReport>('/config/admin/validate', body),
  publish: (body: { cards: unknown[]; flags?: unknown[]; note: string }) =>
    api.post<PublishResponse>('/config/admin/publish', body),
  publishFlags: (body: { flags: unknown[]; note: string }) =>
    api.post<PublishResponse>('/config/admin/flags', body),
  rollback: (body: { revision: number; note?: string }) =>
    api.post<PublishResponse>('/config/admin/rollback', body),
};
